import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowUpRight,
  Command,
  Library,
  Menu,
  Moon,
  Search,
  Sun,
  X,
} from 'lucide-react';
import { useTheme } from '@/providers/theme-provider';
import { useAuth } from '@/hooks/useAuth';
import { BRAND } from '@/styles/brand-tokens';
import { LIBRARY_EMAIL, LIBRARY_PHONE, telHref } from '@/components/smchs/contact-lib';

const dashboardPathForUser = (user: any): string => {
  const role: string | undefined = user?.user_type || user?.role;
  const roles: string[] = Array.isArray(user?.roles) ? user.roles : [];
  const isAdmin = role === 'admin' || roles.includes('admin');
  const isLibrarian = role === 'librarian' || roles.includes('librarian');
  if (isAdmin) return '/admin';
  if (isLibrarian) return '/librarian';
  if (role === 'staff' || role === 'faculty') return '/admin';
  if (role === 'student') return '/student';
  return '/catalog';
};

/* ===================================================================== *
 *  SmchsShell: shared layout, nav, footer, theme system, primitives
 *  Used by all pages in the SMCHS Library product.
 * ===================================================================== */

interface SmchsLayoutProps {
  children: React.ReactNode;
}

export const SmchsLayout: React.FC<SmchsLayoutProps> = ({ children }) => (
  <div className="smchs-home">
    <SmchsStyleBlock />
    <SmchsNav />
    <main className="relative">{children}</main>
    <SmchsFooter />
  </div>
);

/* ===================================================================== */
/*  NAV                                                                   */
/* ===================================================================== */

const NAV_LINKS = [
  { label: 'Catalog', to: '/catalog' },
  { label: 'Book Clubs', to: '/book-clubs' },
  { label: 'Challenges', to: '/challenges' },
  { label: 'About', to: '/about' },
];

export const SmchsNav: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const dashboardPath = isAuthenticated && user ? dashboardPathForUser(user) : null;

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-40 bg-[var(--nav-surface)] text-[var(--nav-fg)] transition-shadow duration-300 ${scrolled ? 'shadow-[0_4px_20px_-8px_rgba(0,0,0,0.12)]' : ''
        }`}
    >
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[var(--gold)]/50 to-transparent" />
      <div className="h-[68px] flex items-center">
        {/* Logo: flush left, full height */}
        <Link to="/" className="flex items-center h-[68px] group">
          <img src="/logo_stmarys.png" alt="SMCHS Library" className="h-[50px] w-auto shrink-0" />
        </Link>

        {/* Nav links: centered */}
        <nav className="hidden lg:flex items-center justify-center gap-1 text-[14px] flex-1">
          {NAV_LINKS.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.label}
                to={l.to}
                className={`relative px-3.5 py-2 rounded-lg transition-colors ${active
                  ? 'text-[var(--nav-fg)] bg-[var(--nav-fg)]/10 font-medium'
                  : 'text-[var(--nav-fg)]/70 hover:text-[var(--nav-fg)] hover:bg-[var(--nav-fg)]/5'
                  }`}
              >
                {l.label}
                {active && (
                  <span className="absolute -bottom-[1px] left-3.5 right-3.5 h-[2px] bg-[var(--gold)]" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2 ml-auto pr-5 md:pr-8">
          <button className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--nav-fg)]/15 bg-[var(--nav-fg)]/5 text-[13px] text-[var(--nav-fg)]/70 hover:border-[var(--nav-fg)]/30 hover:bg-[var(--nav-fg)]/10 transition-colors">
            <Search className="w-3.5 h-3.5" />
            Search catalog
            <span className="ml-2 flex items-center gap-0.5 text-[11px] font-mono text-[var(--nav-fg)]/50">
              <Command className="w-3 h-3" /> K
            </span>
          </button>
          <ThemeToggle />
          {dashboardPath ? (
            <Link
              to={dashboardPath}
              className="hidden lg:inline-flex px-4 py-2 rounded-lg text-[14px] bg-[var(--primary)] text-[var(--nav-surface)] hover:brightness-110 transition-all font-semibold shadow-[0_4px_14px_-4px_rgba(30,45,92,0.4)]"
            >
              Dashboard
            </Link>
          ) : (
            <>
              <Link
                to="/signin"
                className="hidden lg:inline-flex px-4 py-2 rounded-lg text-[14px] text-[var(--nav-fg)]/70 hover:text-[var(--nav-fg)]"
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                className="hidden lg:inline-flex px-4 py-2 rounded-lg text-[14px] bg-[var(--primary)] text-[var(--nav-surface)] hover:brightness-110 transition-all font-semibold shadow-[0_4px_14px_-4px_rgba(30,45,92,0.4)]"
              >
                Get a card
              </Link>
            </>
          )}
          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            className="lg:hidden w-10 h-9 rounded-lg border border-[var(--nav-fg)]/15 bg-[var(--nav-fg)]/5 hover:bg-[var(--nav-fg)]/10 hover:border-[var(--nav-fg)]/30 transition-colors flex items-center justify-center text-[var(--nav-fg)]/80"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={mobileOpen ? 'close' : 'open'}
                initial={{ opacity: 0, rotate: -90 }}
                animate={{ opacity: 1, rotate: 0 }}
                exit={{ opacity: 0, rotate: 90 }}
                transition={{ duration: 0.15 }}
                className="flex items-center justify-center"
              >
                {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </motion.span>
            </AnimatePresence>
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' as const }}
            className="lg:hidden overflow-hidden border-t border-[var(--nav-fg)]/10"
          >
            <nav className="px-5 py-4 space-y-1">
              {NAV_LINKS.map((l) => {
                const active = location.pathname === l.to;
                return (
                  <Link
                    key={l.label}
                    to={l.to}
                    className={`block px-4 py-3 rounded-lg text-[15px] transition-colors ${active
                      ? 'text-[var(--nav-fg)] bg-[var(--nav-fg)]/10 font-medium'
                      : 'text-[var(--nav-fg)]/70 hover:text-[var(--nav-fg)] hover:bg-[var(--nav-fg)]/5'
                      }`}
                  >
                    {l.label}
                  </Link>
                );
              })}
            </nav>
            <div className="px-5 pb-5 pt-2 border-t border-[var(--nav-fg)]/10 flex gap-3">
              {dashboardPath ? (
                <Link
                  to={dashboardPath}
                  className="flex-1 text-center py-2.5 rounded-lg text-[14px] bg-[var(--primary)] text-[var(--nav-surface)] hover:brightness-110 transition-all font-semibold"
                >
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/signin"
                    className="flex-1 text-center py-2.5 rounded-lg text-[14px] text-[var(--nav-fg)]/70 hover:text-[var(--nav-fg)] border border-[var(--nav-fg)]/15 hover:bg-[var(--nav-fg)]/5 transition-colors"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/signup"
                    className="flex-1 text-center py-2.5 rounded-lg text-[14px] bg-[var(--primary)] text-[var(--nav-surface)] hover:brightness-110 transition-all font-semibold"
                  >
                    Get a card
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

/* ===================================================================== */
/*  THEME TOGGLE                                                          */
/* ===================================================================== */

export const ThemeToggle: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const [resolved, setResolved] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    if (theme === 'system') {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      const update = () => setResolved(mql.matches ? 'dark' : 'light');
      update();
      mql.addEventListener('change', update);
      return () => mql.removeEventListener('change', update);
    }
    setResolved(theme);
  }, [theme]);

  const isDark = resolved === 'dark';

  return (
    <button
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      className="relative w-10 h-9 rounded-lg border border-[var(--nav-fg)]/15 bg-[var(--nav-fg)]/5 hover:bg-[var(--nav-fg)]/10 hover:border-[var(--nav-fg)]/30 transition-colors flex items-center justify-center text-[var(--nav-fg)]/80 hover:text-[var(--nav-fg)]"
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={isDark ? 'moon' : 'sun'}
          initial={{ opacity: 0, rotate: -90, scale: 0.6 }}
          animate={{ opacity: 1, rotate: 0, scale: 1 }}
          exit={{ opacity: 0, rotate: 90, scale: 0.6 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 flex items-center justify-center"
        >
          {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
        </motion.span>
      </AnimatePresence>
    </button>
  );
};

/* ===================================================================== */
/*  KICKER                                                                */
/* ===================================================================== */

/**
 * Section label. Deliberately un-numbered: the sections on these pages are not
 * a sequence, and numbering them implied an order the content doesn't have.
 */
export const Kicker: React.FC<{ label: string }> = ({ label }) => (
  <div className="inline-flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--muted)]">
    <span className="w-6 h-px bg-[var(--gold)]" />
    <span>{label}</span>
  </div>
);

/* ===================================================================== */
/*  FOOTER                                                                */
/* ===================================================================== */

export const SmchsFooter: React.FC = () => (
  <footer className="border-t border-[var(--border)] bg-[var(--surface-2)]">
    <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-14 grid grid-cols-2 md:grid-cols-5 gap-8">
      <div className="col-span-2">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-[var(--ink)] text-[var(--paper)] flex items-center justify-center">
            <Library className="w-4.5 h-4.5" />
          </div>
          <div className="font-display font-bold text-[1.05rem]">SMCHS Library</div>
        </div>
        <p className="mt-4 text-[14px] text-[var(--ink-soft)] max-w-[36ch]">
          The library at St Mary's Catholic High School, Dubai.
        </p>
      </div>
      <FooterCol
        title="Browse"
        items={[
          { label: 'Catalog', to: '/catalog' },
          { label: 'Book clubs', to: '/book-clubs' },
          { label: 'Challenges', to: '/challenges' },
          { label: 'Resources', to: '/resources' },
        ]}
      />
      <FooterCol
        title="Library"
        items={[
          { label: 'About', to: '/about' },
          { label: 'Visit us', to: '/about' },
        ]}
      />
      <FooterCol
        title="Account"
        items={[
          { label: 'Sign in', to: '/signin' },
          { label: 'Get a card', to: '/signup' },
        ]}
      />
    </div>
    <div className="border-t border-[var(--border)]">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-5 flex flex-wrap items-center justify-between gap-3 text-[11px] font-mono uppercase tracking-[0.15em] text-[var(--muted)]">
        <span>© 2026 SMCHS · All rights reserved</span>
        <span className="normal-case tracking-normal font-sans text-[13px]">
          <a href={`mailto:${LIBRARY_EMAIL}`} className="hover:text-[var(--ink)] transition-colors">{LIBRARY_EMAIL}</a>
          {' · '}
          <a href={telHref(LIBRARY_PHONE)} className="hover:text-[var(--ink)] transition-colors">{LIBRARY_PHONE}</a>
        </span>
      </div>
    </div>
  </footer>
);

const FooterCol: React.FC<{
  title: string;
  items: { label: string; to: string }[];
}> = ({ title, items }) => (
  <div>
    <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--muted)] mb-4">
      {title}
    </div>
    <ul className="space-y-2.5 text-[13px]">
      {items.map((i) => (
        <li key={i.label}>
          <Link
            to={i.to}
            className="text-[var(--ink-soft)] hover:text-[var(--ink)] transition-colors"
          >
            {i.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

/* ===================================================================== */
/*  PAGE HERO: reusable hero block for non-Index pages                   */
/* ===================================================================== */

interface PageHeroProps {
  kickerLabel: string;
  title: React.ReactNode;
  tagline?: string;
  meta?: React.ReactNode;
  children?: React.ReactNode;
}

export const PageHero: React.FC<PageHeroProps> = ({
  kickerLabel,
  title,
  tagline,
  meta,
  children,
}) => (
  <section className="relative overflow-hidden border-b border-[var(--border)]">
    <div className="absolute inset-0 -z-10">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(30,45,92,0.12),transparent_60%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:56px_56px] [mask-image:radial-gradient(ellipse_60%_80%_at_50%_0%,black,transparent_70%)] opacity-40" />
    </div>
    <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-16 md:pt-20 pb-10 md:pb-14">
      <Kicker label={kickerLabel} />
      <h1 className="mt-5 font-display font-[900] text-[2.5rem] sm:text-[3.5rem] md:text-[4.5rem] leading-[0.96] tracking-[-0.03em] text-[var(--ink)] max-w-[20ch]">
        {title}
      </h1>
      {tagline && (
        <p className="mt-6 max-w-[58ch] text-[1rem] md:text-[1.1rem] leading-[1.55] text-[var(--ink-soft)]">
          {tagline}
        </p>
      )}
      {meta && <div className="mt-8">{meta}</div>}
      {children && <div className="mt-10">{children}</div>}
    </div>
  </section>
);

/* ===================================================================== */
/*  STYLE BLOCK: palette + fonts + dark theme                            */
/* ===================================================================== */

export const SmchsStyleBlock: React.FC = () => (
  <style>{`
    .smchs-home {
      /* Light theme: SMCHS brand */
      --bg: ${BRAND.parchment};
      --surface: ${BRAND.paper};
      --surface-2: #ECE8DA;
      --border: #D8D2BE;
      --ink: ${BRAND.ink.light};
      --ink-soft: #2D3955;
      /* 4.8:1 on --bg, 5.4:1 on --surface: AA at the 10-11px sizes this is used for */
      --muted: ${BRAND.muted.light};
      --paper: ${BRAND.paper};
      --primary: ${BRAND.navy.light};
      --primary-soft: ${BRAND.navySoft.light};
      --nav-bg: ${BRAND.navy.light};
      --nav-surface: ${BRAND.paper};
      --nav-fg: ${BRAND.navy.light};
      --gold: ${BRAND.gold.light};
      --gold-soft: ${BRAND.goldSoft};
      /* Gold as *type*. --gold itself is 2.02:1 on --bg: decoration only.
         See BRAND.goldText. 4.97:1 on --bg, 5.56:1 on --surface. */
      --gold-text: ${BRAND.goldText.light};

      background: var(--bg);
      color: var(--ink);
      min-height: 100dvh;
      font-family: 'Satoshi', ui-sans-serif, system-ui, -apple-system, sans-serif;
      font-feature-settings: 'ss01', 'ss02', 'cv01';
      -webkit-font-smoothing: antialiased;
      letter-spacing: -0.005em;
      transition: background-color 0.3s ease, color 0.3s ease;
    }

    .dark .smchs-home {
      /* Dark theme: deep school navy with gold accents.
         --primary stays the navy line of the brand (lifted so it reads on dark);
         --gold stays the crest gold. Keeping them distinct preserves the
         two-accent hierarchy that light mode has. */
      --bg: ${BRAND.deepNavy};
      --surface: #14213A;
      --surface-2: #1B2B4D;
      --border: #2A3552;
      --ink: ${BRAND.ink.dark};
      --ink-soft: #BCC2D3;
      --muted: ${BRAND.muted.dark};
      --paper: ${BRAND.deepNavy};
      --primary: ${BRAND.navy.dark};
      --primary-soft: ${BRAND.navySoft.dark};
      --nav-bg: #060B1A;
      --nav-surface: #0E1730;
      --nav-fg: #F0EBE0;
      --gold: ${BRAND.gold.dark};
      --gold-soft: ${BRAND.goldSoft};
      /* gold.dark already clears 7.3:1+ on every dark surface. */
      --gold-text: ${BRAND.goldText.dark};
    }

    /* font-display / font-serif / font-mono are no longer re-declared here.
       They now resolve from tailwind.config.ts so the staff app gets the same
       brand faces instead of falling through to Inter and Georgia. */

    .smchs-home ::selection {
      /* Always dark-on-gold: the highlight is gold in both themes. */
      background: var(--gold);
      color: ${BRAND.deepNavy};
    }

    .smchs-home * {
      box-sizing: border-box;
    }

    /* Visible keyboard focus everywhere. Pointer users never see it. */
    .smchs-home :focus-visible {
      outline: 2px solid var(--gold);
      outline-offset: 2px;
      border-radius: 4px;
    }

    @media (prefers-reduced-motion: reduce) {
      .smchs-home *, .smchs-home *::before, .smchs-home *::after {
        animation-duration: 0.01ms !important;
        transition-duration: 0.01ms !important;
      }
    }
  `}</style>
);
